import React, { useState, useEffect } from 'react';

// Aboyne Game Component
// ─── Rules ────────────────────────────────────────────────────────────────────
// Board      : Hexagonal board with 5 cells per side (9 rows: 5,6,7,8,9,8,7,6,5),
//              61 cells total.
// Goal cell  : The marked tip on the OPPOSITE side — White's goal is the right tip
//              of row 4 (col 8); Black's goal is the left tip of row 4 (col 0).
// Setup      : White pieces on the left edge of each row; Black pieces on the right
//              edge.  In the middle row (row 4) the tips are goal cells so pieces
//              sit at col 1 (White) and col 7 (Black).
// Blocked    : A stone is blocked when at least one of its neighbours holds an
//              enemy piece.  Blocked stones cannot move.
// Turn       : Move one non-blocked stone:
//              - STEP  — move to an adjacent empty cell.
//              - JUMP  — leap over a consecutive line of friendly stones and land
//                        on the next cell.  If that cell holds an enemy stone it
//                        is captured (removed).
//              A stone may NOT enter the opponent's goal cell.
// Win        : Move a stone onto your own goal cell, OR stalemate the opponent
//              (all their non-blocked stones have no legal destination).
// ─────────────────────────────────────────────────────────────────────────────

const ROW_WIDTHS = [5, 6, 7, 8, 9, 8, 7, 6, 5];
const NUM_ROWS   = 9;
const N          = 61;

// ROW_OFFSETS[r] = flat index of the first cell in row r
// = [0, 5, 11, 18, 26, 35, 43, 50, 56]
const ROW_OFFSETS = (() => {
  const offs = [0];
  for (let r = 0; r < NUM_ROWS - 1; r++) offs.push(offs[r] + ROW_WIDTHS[r]);
  return offs;
})();

// ── Coordinate helpers ────────────────────────────────────────────────────────

const toIdx = (row, col) => ROW_OFFSETS[row] + col;

const fromIdx = (i) => {
  let row = 0;
  while (row < NUM_ROWS - 1 && ROW_OFFSETS[row + 1] <= i) row++;
  return { row, col: i - ROW_OFFSETS[row] };
};

const inBounds = (row, col) =>
  row >= 0 && row < NUM_ROWS && col >= 0 && col < ROW_WIDTHS[row];

/**
 * Convert (row, col) offset coordinates to axial (q, r) coordinates.
 * The board centre (row 4) is (q=0, r=0).
 * q_min for row r equals max(-4, -r).
 */
const toAxial = (row, col) => ({
  q: Math.max(-4, -row) + col,
  r: row - 4,
});

/** Convert axial (q, r) back to (row, col). Returns {row:-1,col:-1} if invalid. */
const fromAxial = (q, r) => {
  const row = r + 4;
  if (row < 0 || row >= NUM_ROWS) return { row: -1, col: -1 };
  const col = q - Math.max(-4, -row);
  return { row, col };
};

// Six hex directions in axial coordinates (pointy-top hexagons)
const HEX_DIRS = [
  { dq:  1, dr:  0 },  // E
  { dq: -1, dr:  0 },  // W
  { dq:  0, dr:  1 },  // SE
  { dq:  0, dr: -1 },  // NW
  { dq:  1, dr: -1 },  // NE
  { dq: -1, dr:  1 },  // SW
];

// ── Goal cells & initial board ────────────────────────────────────────────────

// White starts LEFT → White's goal is the rightmost tip of row 4 (col 8).
// Black starts RIGHT → Black's goal is the leftmost tip of row 4 (col 0).
const WHITE_GOAL = toIdx(4, ROW_WIDTHS[4] - 1);  // (row 4, col 8)
const BLACK_GOAL = toIdx(4, 0);                    // (row 4, col 0)

const goalOf    = (p) => (p === 'white' ? WHITE_GOAL : BLACK_GOAL);
const oppGoalOf = (p) => (p === 'white' ? BLACK_GOAL : WHITE_GOAL);

const createInitialBoard = () => {
  const b = Array(N).fill(null);
  for (let row = 0; row < NUM_ROWS; row++) {
    if (row === 4) {
      // Goal cells (col 0 and col 8) stay empty; pieces at col 1 and col 7
      b[toIdx(4, 1)]                     = 'white';
      b[toIdx(4, ROW_WIDTHS[4] - 2)]     = 'black';
    } else {
      b[toIdx(row, 0)]                   = 'white';
      b[toIdx(row, ROW_WIDTHS[row] - 1)] = 'black';
    }
  }
  return b;
};

// ── Game logic ────────────────────────────────────────────────────────────────

const opp = (p) => (p === 'white' ? 'black' : 'white');

/**
 * Returns true when the piece at `idx` (belonging to `player`) is blocked.
 * A stone is blocked when at least one neighbour holds an enemy piece.
 */
const isBlocked = (idx, board, player) => {
  const { row, col } = fromIdx(idx);
  const { q, r }     = toAxial(row, col);
  const enemy        = opp(player);
  for (const { dq, dr } of HEX_DIRS) {
    const nb = fromAxial(q + dq, r + dr);
    if (!inBounds(nb.row, nb.col)) continue;
    if (board[toIdx(nb.row, nb.col)] === enemy) return true;
  }
  return false;
};

/**
 * Returns valid destinations for the non-blocked piece at `idx`.
 * Each entry is { to: idx, capture: idx | null }.
 *
 * - STEP : adjacent empty cell (not opponent's goal).
 * - JUMP : leap over a consecutive run of friendly pieces; land on the next
 *          cell.  If that cell holds an enemy, it is captured.
 *          Cannot land on the opponent's goal cell.
 */
const getPieceMoves = (idx, board, player) => {
  const { row, col } = fromIdx(idx);
  const { q, r }     = toAxial(row, col);
  const enemy        = opp(player);
  const oppGoal      = oppGoalOf(player);
  const dests        = [];

  for (const { dq, dr } of HEX_DIRS) {
    // Count consecutive friendly pieces in this direction (board has ≤ 9 cells per row).
    let k = 0;
    while (k < NUM_ROWS) {
      const nb = fromAxial(q + dq * (k + 1), r + dr * (k + 1));
      if (!inBounds(nb.row, nb.col)) break;
      if (board[toIdx(nb.row, nb.col)] !== player) break;
      k++;
    }

    // Landing cell is k+1 steps away.
    const land    = fromAxial(q + dq * (k + 1), r + dr * (k + 1));
    if (!inBounds(land.row, land.col)) continue;
    const landIdx = toIdx(land.row, land.col);
    if (landIdx === oppGoal) continue;   // cannot enter opponent's goal

    if (k === 0) {
      // Step: adjacent cell must be empty.
      if (board[landIdx] === null) {
        dests.push({ to: landIdx, capture: null });
      }
    } else {
      // Jump over k friendly pieces; land on empty cell or capture enemy.
      if (board[landIdx] === null) {
        dests.push({ to: landIdx, capture: null });
      } else if (board[landIdx] === enemy) {
        dests.push({ to: landIdx, capture: landIdx });
      }
    }
  }
  return dests;
};

/** All legal moves for `player` on `board` — { from, to, capture }. */
const getAllMoves = (player, board) => {
  const moves = [];
  for (let i = 0; i < N; i++) {
    if (board[i] !== player) continue;
    if (isBlocked(i, board, player)) continue;
    for (const dest of getPieceMoves(i, board, player)) {
      moves.push({ from: i, ...dest });
    }
  }
  return moves;
};

// ── Component ─────────────────────────────────────────────────────────────────

const Aboyne = () => {
  const [gameStarted,   setGameStarted]   = useState(false);
  const [playerTypes,   setPlayerTypes]   = useState({ white: 'human', black: 'human' });
  const [board,         setBoard]         = useState(createInitialBoard);
  const [currentPlayer, setCurrentPlayer] = useState('white');
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [validMovesMap,  setValidMovesMap]  = useState(new Map()); // to → capture
  const [winner,        setWinner]        = useState(null);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const selectPiece = (idx) => {
    const moves = getPieceMoves(idx, board, currentPlayer);
    setSelectedPiece(idx);
    setValidMovesMap(new Map(moves.map(({ to, capture }) => [to, capture])));
  };

  const resetGame = () => {
    setBoard(createInitialBoard());
    setCurrentPlayer('white');
    setSelectedPiece(null);
    setValidMovesMap(new Map());
    setWinner(null);
    setGameStarted(false);
  };

  const startGame = () => setGameStarted(true);

  const handlePlayerTypeChange = (player, type) =>
    setPlayerTypes((prev) => ({ ...prev, [player]: type }));

  /**
   * Apply a completed move, then check for win (goal reached or stalemate).
   * `movedTo` is the destination index of the just-moved piece.
   */
  const applyAction = (newBoard, movedTo, player) => {
    if (movedTo === goalOf(player)) {
      setWinner(player);
    } else {
      const next = opp(player);
      if (getAllMoves(next, newBoard).length === 0) {
        setWinner(player);  // stalemate — current player wins
      } else {
        setCurrentPlayer(next);
      }
    }
    setSelectedPiece(null);
    setValidMovesMap(new Map());
  };

  // ── Human click handler ───────────────────────────────────────────────────

  const handleClick = (idx) => {
    if (winner || !gameStarted) return;
    if (playerTypes[currentPlayer] !== 'human') return;

    if (selectedPiece !== null) {
      if (idx === selectedPiece) {
        // Deselect
        setSelectedPiece(null);
        setValidMovesMap(new Map());
        return;
      }
      if (validMovesMap.has(idx)) {
        // Execute move (step or jump, optionally with capture)
        const capture  = validMovesMap.get(idx);
        const newBoard = [...board];
        newBoard[idx]           = currentPlayer;
        newBoard[selectedPiece] = null;
        if (capture !== null) newBoard[capture] = null;
        setBoard(newBoard);
        applyAction(newBoard, idx, currentPlayer);
        return;
      }
      if (board[idx] === currentPlayer && !isBlocked(idx, board, currentPlayer)) {
        // Switch selection to another own non-blocked piece
        const moves = getPieceMoves(idx, board, currentPlayer);
        if (moves.length > 0) { selectPiece(idx); return; }
      }
      // Click elsewhere → deselect
      setSelectedPiece(null);
      setValidMovesMap(new Map());
      return;
    }

    // No piece selected: click own non-blocked piece to select it
    if (board[idx] === currentPlayer && !isBlocked(idx, board, currentPlayer)) {
      const moves = getPieceMoves(idx, board, currentPlayer);
      if (moves.length > 0) selectPiece(idx);
    }
  };

  // ── Computer player ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!gameStarted || winner) return;
    if (playerTypes[currentPlayer] !== 'computer') return;

    const id = setTimeout(() => {
      const moves = getAllMoves(currentPlayer, board);
      if (moves.length === 0) {
        setWinner(opp(currentPlayer));
        return;
      }
      const { from, to, capture } = moves[Math.floor(Math.random() * moves.length)];
      const newBoard = [...board];
      newBoard[to]   = currentPlayer;
      newBoard[from] = null;
      if (capture !== null) newBoard[capture] = null;
      setBoard(newBoard);
      applyAction(newBoard, to, currentPlayer);
    }, 500);

    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, gameStarted, winner, board, playerTypes]);

  // ── Status ────────────────────────────────────────────────────────────────

  const getStatus = () => {
    if (winner) {
      const name = winner === 'white' ? 'White' : 'Black';
      return `${name} wins!`;
    }
    return `Current Turn: ${currentPlayer === 'white' ? 'White' : 'Black'}`;
  };

  // ── Configuration screen ──────────────────────────────────────────────────

  if (!gameStarted) {
    return (
      <div className="game-container">
        <h1 className="game-title">Aboyne</h1>
        <h2 className="config-subtitle">Player Configuration</h2>
        <div className="player-config">
          <div className="player-config-item">
            <h3>White (moves first)</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes.white === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('white', 'human')}
              >
                Human
              </button>
              <button
                className={`player-type-button ${playerTypes.white === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('white', 'computer')}
              >
                Computer
              </button>
            </div>
          </div>
          <div className="player-config-item">
            <h3>Black</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes.black === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('black', 'human')}
              >
                Human
              </button>
              <button
                className={`player-type-button ${playerTypes.black === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('black', 'computer')}
              >
                Computer
              </button>
            </div>
          </div>
        </div>
        <button className="new-game-button" onClick={startGame}>
          Start Game
        </button>
      </div>
    );
  }

  // ── SVG board rendering ───────────────────────────────────────────────────

  // Pointy-top hexagons, axial coordinates.
  // The widest row (row 4) has 9 cells; each row is centred around the same x.
  const R        = 40;                    // circumradius (centre → vertex)
  const W_HEX    = Math.sqrt(3) * R;      // horizontal cell spacing ≈ 69.28
  const V_STEP   = 1.5 * R;              // vertical row spacing = 60
  const PAD      = 12;                    // padding around the board

  const SVG_W    = Math.round(2 * PAD + 9 * W_HEX);           // ≈ 648
  const SVG_H    = Math.round(2 * PAD + 2 * R + 8 * V_STEP);  //  = 584
  const CENTER_X = PAD + 4.5 * W_HEX;    // horizontal centre of the board
  const CENTER_Y = PAD + R + 4 * V_STEP; // vertical centre (row 4)

  /** Pixel centre of cell (row, col) derived from axial coordinates. */
  const cellCenter = (row, col) => {
    const { q, r } = toAxial(row, col);
    return {
      cx: CENTER_X + W_HEX * (q + r * 0.5),
      cy: CENTER_Y + V_STEP * r,
    };
  };

  /** SVG polygon points for a pointy-top hexagon centred at (cx, cy). */
  const hexPoints = (cx, cy, r) =>
    Array.from({ length: 6 }, (_, k) => {
      const a = (Math.PI / 3) * k - Math.PI / 2;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(' ');

  const isHumanTurn = playerTypes[currentPlayer] === 'human';

  // Pre-compute which of the current player's pieces are selectable.
  const moveableSet = new Set();
  if (!winner && isHumanTurn && selectedPiece === null) {
    for (let i = 0; i < N; i++) {
      if (board[i] !== currentPlayer) continue;
      if (!isBlocked(i, board, currentPlayer) &&
          getPieceMoves(i, board, currentPlayer).length > 0) {
        moveableSet.add(i);
      }
    }
  }

  return (
    <div className="game-container">
      <h1 className="game-title">Aboyne</h1>
      <div className={`game-status ${winner ? 'winner' : ''}`}>
        {getStatus()}
      </div>
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="aboyne-svg">
        {board.map((cell, idx) => {
          const { row, col } = fromIdx(idx);
          const { cx, cy }   = cellCenter(row, col);
          const isSelected   = idx === selectedPiece;
          const isValid      = validMovesMap.has(idx);
          const isWhiteGoal  = idx === WHITE_GOAL;
          const isBlackGoal  = idx === BLACK_GOAL;

          let cellFill = '#c8a26e';
          if      (isSelected)   cellFill = '#86efac';
          else if (isValid)      cellFill = '#fde68a';
          else if (isWhiteGoal)  cellFill = '#bfdbfe';
          else if (isBlackGoal)  cellFill = '#fecaca';

          const clickable =
            !winner && isHumanTurn &&
            (selectedPiece === null
              ? moveableSet.has(idx)
              : isValid || (board[idx] === currentPlayer && moveableSet.has(idx)));

          return (
            <g
              key={idx}
              onClick={() => handleClick(idx)}
              style={{ cursor: clickable ? 'pointer' : 'default' }}
            >
              {/* Hex cell background */}
              <polygon
                points={hexPoints(cx, cy, R - 1.5)}
                fill={cellFill}
                stroke="#7a5534"
                strokeWidth={isSelected ? 3 : 1.5}
              />

              {/* Goal-cell inner ring (dashed) — visible even under pieces */}
              {(isWhiteGoal || isBlackGoal) && (
                <polygon
                  points={hexPoints(cx, cy, R * 0.55)}
                  fill="none"
                  stroke={isWhiteGoal ? '#2563eb' : '#dc2626'}
                  strokeWidth={2}
                  strokeDasharray="4,3"
                />
              )}

              {/* Piece */}
              {cell === 'white' && (
                <circle
                  cx={cx} cy={cy} r={R * 0.52}
                  fill="#f8fafc" stroke="#64748b" strokeWidth={2.5}
                />
              )}
              {cell === 'black' && (
                <circle
                  cx={cx} cy={cy} r={R * 0.52}
                  fill="#1e293b" stroke="#94a3b8" strokeWidth={2.5}
                />
              )}

              {/* Valid-move dot on empty cells */}
              {isValid && cell === null && (
                <circle cx={cx} cy={cy} r={7} fill="rgba(0,0,0,0.25)" />
              )}
            </g>
          );
        })}
      </svg>
      <button className="new-game-button" onClick={resetGame}>
        New Game
      </button>
    </div>
  );
};

export default Aboyne;
